import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabaseClient'

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, required }) {
  const [inputValue, setInputValue] = useState(value || '')
  const [suggestions, setSuggestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [error, setError] = useState(null)
  const debounceTimerRef = useRef(null)
  const wrapperRef = useRef(null)

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const fetchSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()

      if (sessionError || !session) {
        console.error('Session error:', sessionError)
        setError('Please sign in to use address autocomplete')
        return
      }

      console.log('Making geocode request with user:', session.user.id)

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mapbox-geocode?query=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Geocoding error response:', { status: response.status, data: errorData })
        throw new Error(errorData.error || `Failed to fetch suggestions (${response.status})`)
      }

      const data = await response.json()

      if (data.features && data.features.length > 0) {
        setSuggestions(data.features)
        setShowSuggestions(true)
      } else {
        setSuggestions([])
        setShowSuggestions(false)
      }
    } catch (err) {
      console.error('Geocoding error:', err)
      if (err.message === 'Failed to fetch') {
        setError('Network error: Please check your connection and ensure Mapbox API key is configured in Settings')
      } else {
        setError(err.message)
      }
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onChange?.(newValue)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(newValue)
    }, 300)
  }

  const handleSelectSuggestion = (feature) => {
    let address = feature.place_name
    address = address.replace(/, New Zealand$/i, '')
    const [longitude, latitude] = feature.center

    setInputValue(address)
    setShowSuggestions(false)
    setSuggestions([])

    onChange?.(address)
    onSelect?.({
      address,
      latitude,
      longitude,
      placeName: address,
    })
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true)
            }
          }}
          placeholder={placeholder || 'Enter your address'}
          required={required}
          className="input pr-10"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="spinner spinner-sm text-primary-600"></div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-1 text-xs text-red-600">
          {error}
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((feature, index) => (
            <button
              key={feature.id || index}
              type="button"
              onClick={() => handleSelectSuggestion(feature)}
              className="w-full px-4 py-3 text-left hover:bg-secondary-50 transition-colors border-b border-secondary-100 last:border-b-0 focus:outline-none focus:bg-secondary-50"
            >
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary-900 truncate">
                    {feature.text}
                  </p>
                  <p className="text-xs text-secondary-500 truncate">
                    {feature.place_name.replace(/, New Zealand$/i, '')}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
